export const participantFields = [
  "ParticipantId",
  "Name",
  "Alias",
  "Address",
  "Email",
  "MobilePhone",
  "DocumentId",
  "ContactPerson",
  "ContactPhone",
  "BankAccount",
  "PostalCode",
  "City",
  "Country",
  "CityCode",
  "Save",
];

export const constructParticipantDetails = (participantType, details) => {
  return {
    [`${participantType}ParticipantId`]: details?.participantId,
    [`${participantType}Name`]: details?.name,
    [`${participantType}Address`]: details?.address,
    [`${participantType}Email`]: details?.email,
    [`${participantType}MobilePhone`]: details?.mobilePhone,
    [`${participantType}DocumentId`]: details?.documentId,
    [`${participantType}ContactPerson`]: details?.contactPerson,
    [`${participantType}ContactPhone`]: details?.contactPhone,
    [`${participantType}BankAccount`]: details?.bankAccount,
    [`${participantType}PostalCode`]: details?.postalCode,
    [`${participantType}City`]: details?.city,
    [`${participantType}Country`]: details?.country,
    [`${participantType}CityCode`]: details?.cityCode,
    [`${participantType}Save`]: details?.save,
  };
};

const getParticipantNameFields = (participantType) => {
  const refinedFields = participantFields?.map(
    (field) => `${participantType}${field}`
  );

  return refinedFields;
};

export const getParticipantType = (participantFieldName) => {
  if (participantFieldName.includes("sender")) {
    return "sender";
  }

  if (participantFieldName.includes("recipient")) {
    return "recipient";
  }

  return "";
};

export const resetParticipantDetails = (
  participantType,
  resetSave = false,
  saveValue
) => {
  const resetDetails = {};

  participantFields.forEach(
    (key) => (resetDetails[`${participantType}${key}`] = undefined)
  );

  if (!resetSave) {
    return {
      ...resetDetails,
      [`${participantType}Save`]: saveValue,
    };
  }

  return resetDetails;
};

export default function resetParticipantFields(participantFieldName, setters) {
  const { setFieldsValue, setSearch } = setters;
  if (!participantFieldName) {
    return false;
  }

  const participantType = getParticipantType(participantFieldName);

  const participantNameFields = getParticipantNameFields(participantType);

  participantNameFields.forEach((field) => {
    setFieldsValue({ [field]: undefined });
  });

  setSearch((prevState) => ({
    ...prevState,
    value: undefined,
    isSelectOpen: true,
  }));
}

export function buildParticipantFormAndReduxPayload({
  participantType,
  refinedDetails,
  rawParticipant,
  source,
}) {
  const participantId =
    refinedDetails[`${participantType}ParticipantId`] ??
    rawParticipant?.participantId;

  // labelInValue para nombre
  const nameLabelValue =
    rawParticipant?.name && participantId
      ? { key: String(participantId), label: rawParticipant.name }
      : undefined;

  // labelInValue para alias (solo si no está vacío)
  const aliasLabelValue =
    rawParticipant?.alias && rawParticipant.alias.trim() !== "" && participantId
      ? { key: String(participantId), label: rawParticipant.alias }
      : undefined;

  // base
  const nextReduxPayload = {
    ...refinedDetails,
    [`${participantType}NewParticipant`]: false,
  };

  const nextFormValues = {
    ...refinedDetails,
  };

  // reglas según el origen
  if (source === "alias") {
    // alias se queda SI lo hay
    nextReduxPayload[`${participantType}Alias`] = aliasLabelValue;
    nextFormValues[`${participantType}Alias`] = aliasLabelValue;

    // y siempre se muestra el name si lo tenemos
    if (nameLabelValue) {
      nextReduxPayload[`${participantType}Name`] = nameLabelValue;
      nextFormValues[`${participantType}Name`] = nameLabelValue;
    }
  } else if (source === "name") {
    // si viene de nombre, se muestra nombre
    if (nameLabelValue) {
      nextReduxPayload[`${participantType}Name`] = nameLabelValue;
      nextFormValues[`${participantType}Name`] = nameLabelValue;
    }
    // pero forzamos a vaciar alias
    nextReduxPayload[`${participantType}Alias`] = undefined;
    nextFormValues[`${participantType}Alias`] = undefined;
  } else {
    // caso genérico
    if (nameLabelValue) {
      nextReduxPayload[`${participantType}Name`] = nameLabelValue;
      nextFormValues[`${participantType}Name`] = nameLabelValue;
    }
  }

  return { nextReduxPayload, nextFormValues };
}

export function transformParticipantsToOptions(
  participants = [],
  searchValue = "",
  { labelField = "name", searchField = "name", strictLabel = false } = {}
) {
  if (!Array.isArray(participants) || participants.length === 0) return [];

  const normalizedQuery = (searchValue || "").toLowerCase();

  const getLabel = (participant) => {
    const raw = participant?.[labelField];
    // en modo estricto NO se hace fallback
    const label = strictLabel ? raw ?? "" : raw || participant?.name || "";
    return typeof label === "string" ? label : String(label ?? "");
  };

  const hasVisibleLabel = (participant) =>
    getLabel(participant).trim().length > 0;

  const matchesQuery = (participant) => {
    if (!normalizedQuery) return true;
    const searchVal = (participant?.[searchField] || "").toLowerCase();
    const labelVal =
      labelField !== searchField
        ? (participant?.[labelField] || "").toLowerCase()
        : "";
    return (
      searchVal.includes(normalizedQuery) || labelVal.includes(normalizedQuery)
    );
  };

  return participants
    .filter(hasVisibleLabel)
    .filter(matchesQuery)
    .map((participant) => ({
      key: participant.participantId,
      label: getLabel(participant),
    }));
}

export function buildAsyncSelectNotFoundContent(searchState, fallbackText) {
  if (!searchState) return null;
  const { hasSearched, value, matchesCount } = searchState;
  const hasQuery = typeof value === "string" && value.trim().length > 0;

  if (hasSearched && hasQuery && matchesCount === 0) {
    return fallbackText;
  }
  return null;
}
